import { GraphQLSchema } from 'graphql/type/schema';
import * as jsoncParser from 'jsonc-parser';
import _ from 'lodash';
import * as monacoEditor from 'monaco-editor';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ExplorerSettingsType } from 'src/app/graph/explorerPage/explorerSettings/useExplorerSettings';
import { ExplorerDocsTab } from 'src/app/graph/explorerPage/helpers/ExplorerDocsTab';
import { usePreflightSandbox } from 'src/app/graph/explorerPage/hooks/useExplorerState/usePreflightSandbox/usePreflightSandbox';
import {
  useLocalStorageGraphIdentifier,
  usePerGraphIdentifierLocalStorage,
} from 'src/app/graph/explorerPage/hooks/usePerGraphIdentifierLocalStorage';
import { ExplorerResultsTab } from 'src/app/graph/explorerPage/resultsPane/ExplorerResultsTab';
import { useSetEndpointReachability } from 'src/app/graph/hooks/useEndpointReachability';
import { GraphRef } from 'src/app/graph/hooks/useGraphRef';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { useTabbedStudioEditor } from 'src/hooks/useTabbedStudioEditor';
import { getRootTypeNames } from 'src/lib/graphql/graphqlHelpers';
import { useLDFlag } from 'src/lib/launchDarkly';

import { ExplorerBehavioralFlags } from '../../explorerBehavioralFlags';
import { getDocumentDefinitions } from '../../helpers/graphqlParseHelpers';
import {
  OperationPath,
  graphQLPathToOperationPath,
} from '../../helpers/operationPathHelpers';
import {
  EXPLORER_LISTENING_FOR_STATE,
  IncomingEmbedMessageEvent,
  SET_OPERATION,
  sendPostMessageFromEmbedToParent,
} from '../../helpers/postMessageHelpers';
import { JSONObject } from '../../helpers/types';
import {
  getMonacoCodeEditorOpener,
  getMonacoOverrideServices,
} from '../../operationsPane/graphqlEditor/schemaDefinitionProviderFactory';
import { getGroupedPaths } from '../../search/docsPathSelection/usePathsToType';
import { useDerivedEditorValue } from '../useDerivedEditorValue';

import { traverseVariablesAndAttemptInterpolation } from './interpolation/traverseVariablesAndAttemptInterpolation';
import { performMockOperation } from './performMockOperation';
import {
  CancelledResponse,
  OnResponseUpdate,
  OperationRequestBody,
  PerformOperationReturn,
  ResponseAndMetrics,
  isPotentialCORSError,
  performOperation,
} from './performOperation/performOperation';
import { performPostMessageOperation } from './performPostMessageOperation';
import { DocsNavigationAction } from './useDocsPath';
import {
  EditorTabState,
  useEditorTabState,
} from './useEditorTabState/useEditorTabState';
import { useEnvironmentVariables } from './useEnvironmentVariables';
import { useHasPublicUrlBeenConfirmed } from './useHasPublicUrlBeenConfirmed';
import { useHeadersManagerContext } from './useHeadersManagerContext/useHeadersManagerContext';
import {
  OperationHistoryItem,
  useOperationHistory,
} from './useOperationHistory';
import { SearchStateAction, useSearchState } from './useSearchState';
import { useSubscriptions } from './useSubscriptions';
import { useSyncSavedOperationsOnLoad } from './useSyncSavedOperationsOnLoad';

const MissingEndpointError = new Error('endpoint url missing');
function useExplorerTabbedEditors({
  schema,
  dispatchDocsNavAction,
  editorTabState,
  explorerBehavioralFlags,
}: {
  schema: GraphQLSchema;
  dispatchDocsNavAction: (action: DocsNavigationAction) => void;
  editorTabState: EditorTabState;
  explorerBehavioralFlags: ExplorerBehavioralFlags;
}) {
  const schemaRef = React.useRef(schema);
  schemaRef.current = schema;

  const setDocsPathRef = React.useRef<(path: OperationPath) => void>();
  React.useEffect(() => {
    setDocsPathRef.current = (path: OperationPath) =>
      dispatchDocsNavAction({ type: 'navigate to', path });
  }, [dispatchDocsNavAction, setDocsPathRef]);

  const getOverrideServices = React.useCallback(
    () => getMonacoOverrideServices(schemaRef),
    [],
  );

  const getCodeEditorOpener = React.useCallback(
    () => getMonacoCodeEditorOpener(schemaRef, setDocsPathRef),
    [],
  );

  const {
    tabIds,
    selectedTab,
    savedOperationLocalState,
    setSelectedTab,
    getInitialOperationByTab,
    setOperationByTab,
    getInitialVariablesByTab,
    setVariablesByTab,
    getInitialMockedResponseByTab,
    setMockedResponseByTab,
    getInitialPreflightOperationScriptByTab,
    getInitialPostflightOperationScriptByTab,
    dispatchSavedOperationLocalStateAction,
  } = editorTabState;
  const [_numberOfTabs, setNumberOfTabs] = React.useState(tabIds.length);
  const initialTabbedOperationFiles = React.useMemo(
    () =>
      Object.fromEntries(
        tabIds.flatMap((operationId) => {
          const operation = getInitialOperationByTab(operationId);
          return operation !== undefined
            ? [
                [
                  operationId,
                  {
                    id: operationId,
                    text: operation,
                    language: 'graphql' as const,
                  },
                ],
              ]
            : [];
        }),
      ),
    [tabIds, getInitialOperationByTab],
  );

  const { editor: operationEditor, modelsByName: operationModelsById } =
    useTabbedStudioEditor({
      editorId: 'operations',
      getOverrideServices,
      controlContents: false,
      files: initialTabbedOperationFiles,
      selectedFile: selectedTab,
      setSelectedFile: setSelectedTab,
    });

  React.useEffect(() => {
    const disposables = Object.entries(operationModelsById).map(
      ([modelId, model]) =>
        model.onDidChangeContent(() => {
          const newOperationText = model.getValue();
          setOperationByTab(modelId, newOperationText);
        }),
    );
    disposables.push(
      monacoEditor.editor.registerEditorOpener(getCodeEditorOpener()),
    );
    return () => disposables.forEach((disposable) => disposable.dispose());
  }, [getCodeEditorOpener, operationModelsById, setOperationByTab]);

  // When a new tab is made, focus
  React.useEffect(() => {
    setNumberOfTabs((prev) => {
      if (prev < tabIds.length) {
        if (
          // don't auto focus the editor when the default tab loads
          tabIds.length !== 1 ||
          explorerBehavioralFlags.shouldAutoFocusEditor
        ) {
          operationEditor.focus();
        }
      }
      return tabIds.length;
    });
  }, [tabIds, operationEditor, explorerBehavioralFlags.shouldAutoFocusEditor]);

  const initialTabbedVariableFiles = React.useMemo(
    () =>
      Object.fromEntries(
        tabIds.flatMap((operationId) => {
          const operation = getInitialVariablesByTab(operationId);
          return operation !== undefined
            ? [
                [
                  operationId,
                  {
                    id: operationId,
                    text: operation,
                    language: 'json' as const,
                  },
                ],
              ]
            : [];
        }),
      ),
    [tabIds, getInitialVariablesByTab],
  );

  const { editor: variablesEditor, modelsByName: variableModelsById } =
    useTabbedStudioEditor({
      editorId: 'variables',
      controlContents: false,
      files: initialTabbedVariableFiles,
      selectedFile: selectedTab,
      setSelectedFile: setSelectedTab,
    });
  React.useEffect(() => {
    const disposables = Object.entries(variableModelsById).map(
      ([modelId, model]) =>
        model.onDidChangeContent(() => {
          const newOperationText = model.getValue();
          setVariablesByTab(modelId, newOperationText);
        }),
    );
    return () => disposables.forEach((disposable) => disposable.dispose());
  }, [variableModelsById, setVariablesByTab]);

  const initialTabbedMockedResponseFiles = React.useMemo(
    () =>
      Object.fromEntries(
        tabIds.flatMap((operationId) => {
          const operation = getInitialMockedResponseByTab(operationId);
          return operation !== undefined
            ? [
                [
                  operationId,
                  {
                    id: operationId,
                    text: operation,
                    language: 'json' as const,
                  },
                ],
              ]
            : [];
        }),
      ),
    [tabIds, getInitialMockedResponseByTab],
  );

  const {
    editor: mockedResponseEditor,
    modelsByName: mockedResponseModelsById,
  } = useTabbedStudioEditor({
    editorId: 'mockedResponse',
    controlContents: false,
    files: initialTabbedMockedResponseFiles,
    selectedFile: selectedTab,
    setSelectedFile: setSelectedTab,
    contextmenu: false,
    lineNumbers: 'off',
    glyphMargin: false,
  });
  React.useEffect(() => {
    const disposables = Object.entries(mockedResponseModelsById).map(
      ([modelId, model]) =>
        model.onDidChangeContent(() => {
          const newOperationText = model.getValue();
          setMockedResponseByTab(modelId, newOperationText);
        }),
    );
    return () => disposables.forEach((disposable) => disposable.dispose());
  }, [mockedResponseModelsById, setMockedResponseByTab]);

  const initialTabbedPreflightOperationScriptFiles = React.useMemo(
    () =>
      Object.fromEntries(
        tabIds.flatMap((scriptId) => {
          const script = getInitialPreflightOperationScriptByTab(scriptId);
          return script !== undefined
            ? [
                [
                  scriptId,
                  {
                    id: scriptId,
                    text: script,
                    language: 'javascript' as const,
                  },
                ],
              ]
            : [];
        }),
      ),
    [getInitialPreflightOperationScriptByTab, tabIds],
  );

  const {
    editor: preflightOperationScriptEditor,
    modelsByName: preflightOperationScriptModelById,
  } = useTabbedStudioEditor({
    editorId: 'preflight-operation-script',
    controlContents: false,
    files: initialTabbedPreflightOperationScriptFiles,
    selectedFile: selectedTab,
    setSelectedFile: setSelectedTab,
  });

  const initialTabbedPostflightOperationScriptFiles = React.useMemo(
    () =>
      Object.fromEntries(
        tabIds.flatMap((scriptId) => {
          const script = getInitialPostflightOperationScriptByTab(scriptId);
          return script !== undefined
            ? [
                [
                  scriptId,
                  {
                    id: scriptId,
                    text: script,
                    language: 'javascript' as const,
                  },
                ],
              ]
            : [];
        }),
      ),
    [getInitialPostflightOperationScriptByTab, tabIds],
  );

  const {
    editor: postflightOperationScriptEditor,
    modelsByName: postflightOperationScriptModelById,
  } = useTabbedStudioEditor({
    editorId: 'postflight-operation-script',
    controlContents: false,
    files: initialTabbedPostflightOperationScriptFiles,
    selectedFile: selectedTab,
    setSelectedFile: setSelectedTab,
  });

  useSyncSavedOperationsOnLoad({
    savedOperationLocalState,
    dispatchSavedOperationLocalStateAction,
    operationModelsById,
    variableModelsById,
    preflightOperationScriptModelById,
    postflightOperationScriptModelById,
  });

  return {
    editor: operationEditor,
    variablesEditor,
    preflightOperationScriptEditor,
    postflightOperationScriptEditor,
    mockedResponseEditor,
  };
}

/**
 * Encapsulation of all state in Apollo Studio.
 *
 * Ask ourselves in the future: do we actually _want_ to encapsulate this when
 * the goal is to clearly understand state interactions?
 */
export const useExplorerState = ({
  endpointUrl,
  subscriptionUrl,
  includeCookies,
  schema,
  explorerBehavioralFlags,
  explorerSettings: {
    isPreflightScriptEnabled,
    isOperationScriptEnabled,
    preflightScript,
    showTraces,
    requestQueryPlan,
    isMockingResponses,
    requestConnectorsDebugging,
  },
  setDocsTab,
  openEditUrlModal,
  graphRef,
}: {
  endpointUrl: string | null;
  subscriptionUrl?: string | null;
  includeCookies: boolean;
  schema: GraphQLSchema;
  explorerBehavioralFlags: ExplorerBehavioralFlags;
  explorerSettings: ExplorerSettingsType;
  setDocsTab: (newDocsTab: ExplorerDocsTab) => void;
  openEditUrlModal: (openingIntent: 'Set url' | 'Run query') => void;
  graphRef: GraphRef | null;
}) => {
  const shouldShowPostflightScripts = useLDFlag('nebula-postflight-scripts');

  const localStorageGraphIdentifier = useLocalStorageGraphIdentifier({
    shouldNamespaceSandboxByEndpoint: false,
    shouldOnlyStoreForRegisteredGraphs: false,
  });

  const [operationHistory, pushToOperationHistory] = useOperationHistory();

  const {
    checkedHeaders,
    tabbedHeaderDefinitions,
    setTabbedHeaderDefinitions,
    interpolateEnvVariablesIntoHeadersWithRef,
    updateEnvironmentVariables,
  } = useHeadersManagerContext();

  const editorTabState = useEditorTabState();
  const baseDispatchDocsNavAction = editorTabState.baseDispatchDocsNavAction;

  const dispatchDocsNavAction = React.useCallback(
    (action: DocsNavigationAction) => {
      setDocsTab('Documentation');
      baseDispatchDocsNavAction(action);
    },
    [setDocsTab, baseDispatchDocsNavAction],
  );

  const {
    editor,
    variablesEditor,
    mockedResponseEditor,
    preflightOperationScriptEditor,
    postflightOperationScriptEditor,
  } = useExplorerTabbedEditors({
    schema,
    editorTabState,
    dispatchDocsNavAction,
    explorerBehavioralFlags,
  });

  const {
    getEnvironmentVariables,
    environmentVariablesEditor,
    setEnvironmentVariables,
  } = useEnvironmentVariables({
    localStorageGraphIdentifier,
    compactEditor: explorerBehavioralFlags.environmentVariablesInSettings,
  });

  const [resultsTab, setResultsTab] =
    React.useState<ExplorerResultsTab>('Response');

  useEffect(() => {
    if (!showTraces && resultsTab === 'Traces') setResultsTab('Response');
  }, [resultsTab, showTraces]);

  const getDerivedValue = useCallback(
    (editorValue: string) =>
      getDocumentDefinitions(
        editorValue,
        getRootTypeNames(schema),
      ).fragmentDefinitions.map((definition) => definition.type),
    [schema],
  );
  const fragmentTypeNames = useDerivedEditorValue({
    editor,
    getValue: getDerivedValue,
    withPosition: false,
  });

  const { searchState, dispatchSearchAction: baseDispatchSearchAction } =
    useSearchState();
  const dispatchSearchAction = React.useCallback(
    (action: SearchStateAction) => {
      if (action.type === 'select field') {
        const { fieldMatch, parentMatch, argumentMatch } = action.selectedItem;
        const groupedPaths = getGroupedPaths(
          schema,
          fragmentTypeNames,
          parentMatch.type,
          null,
        );
        const argumentPath = argumentMatch?.argument
          ? [argumentMatch.argument]
          : [];
        const groupedItems = groupedPaths.flatMap(({ groupPath, paths }) =>
          paths.map(({ path, isPartial }) => ({
            isPartial,
            path: [...groupPath, ...path, fieldMatch.field, ...argumentPath],
          })),
        );
        if (groupedItems.length === 1 && groupedItems[0]?.isPartial === false) {
          const item = groupedItems[0];
          const path = graphQLPathToOperationPath(item.path, schema);
          baseDispatchSearchAction({
            type: 'select path',
          });
          dispatchDocsNavAction({
            type: 'navigate to',
            path,
          });
          return;
        }
      }

      baseDispatchSearchAction(action);
    },
    [
      baseDispatchSearchAction,
      dispatchDocsNavAction,
      fragmentTypeNames,
      schema,
    ],
  );

  useEffect(() => {
    if (explorerBehavioralFlags.shouldPostMessageOperations) {
      const handleExplorerStatePostMessage = (
        event: IncomingEmbedMessageEvent,
      ) => {
        if (event.data.name === SET_OPERATION) {
          const { variables, operation } = event.data;
          editorTabState.createNewTab({
            operation,
            variables,
          });
        }
      };
      window.addEventListener('message', handleExplorerStatePostMessage);
      // We need to send the parent page a message when the embedded explorer is listening
      // for a state, such as a default operation
      sendPostMessageFromEmbedToParent({
        name: EXPLORER_LISTENING_FOR_STATE,
      });
      return () => {
        window.removeEventListener('message', handleExplorerStatePostMessage);
      };
    }
  }, [
    editor,
    editorTabState,
    explorerBehavioralFlags.shouldPostMessageOperations,
    variablesEditor,
  ]);

  const setEndpointReachability = useSetEndpointReachability();
  const submitOperationAbortControllerRef = useRef<AbortController>();

  const runPreflightScript = usePreflightSandbox({
    endpointUrl,
    includeCookies,
    explorerBehavioralFlags,
    graphRef,
    currentOperationId:
      editorTabState.selectedTab &&
      editorTabState.savedOperationLocalState[editorTabState.selectedTab]
        ?.collectionEntryId,
    setDocsTab,
    isOperationScriptEnabled,
    isInTestContext: false,
  });

  const [promptRequest, setPromptRequest] = useState<{
    message: string;
    defaultValue: string | undefined;
    onSubmit: (response: string | null) => void;
  } | null>(null);

  const [oauth2Request, setOAuth2Request] = useState<{
    authUrl: string;
    queryParams: Record<string, string>;
    onComplete: (
      response: Record<string, string | string[] | null> | null,
    ) => void;
  } | null>(null);

  const getRefreshEnvironmentVariables = React.useCallback(
    async (requestBody: OperationRequestBody) => {
      const environmentVariables = getEnvironmentVariables();
      let newEnvironmentVariables = environmentVariables;
      const runScriptOptions: Omit<
        Parameters<typeof runPreflightScript>[0],
        'script' | 'environmentVariables' | 'scriptType'
      > = {
        isTest: false,
        requestBody,
        handlePrompt: (message, defaultValue) =>
          new Promise((resolve) => {
            setPromptRequest({
              message,
              defaultValue,
              onSubmit(response) {
                resolve(response);
                setPromptRequest(null);
              },
            });
          }),
        handleOAuth2Request: (authUrl, queryParams) =>
          new Promise((resolve) => {
            setOAuth2Request({
              authUrl,
              queryParams,
              onComplete(responseQueryParams) {
                resolve(responseQueryParams);
                setOAuth2Request(null);
              },
            });
          }),
      };
      if (isPreflightScriptEnabled) {
        newEnvironmentVariables = await runPreflightScript({
          script: preflightScript,
          environmentVariables: newEnvironmentVariables,
          scriptType: 'preflight',
          ...runScriptOptions,
        });
      }

      if (isOperationScriptEnabled) {
        newEnvironmentVariables = await runPreflightScript({
          script: preflightOperationScriptEditor.getValue(),
          environmentVariables: newEnvironmentVariables,
          scriptType: 'preflightOperation',
          ...runScriptOptions,
        });
      }

      // if we should run operation script, run it and set result to newEnvVars
      if (!_.isEqual(environmentVariables, newEnvironmentVariables)) {
        setEnvironmentVariables(newEnvironmentVariables);
      }
      return newEnvironmentVariables;
    },
    [
      getEnvironmentVariables,
      isPreflightScriptEnabled,
      isOperationScriptEnabled,
      runPreflightScript,
      preflightScript,
      preflightOperationScriptEditor,
      setEnvironmentVariables,
    ],
  );

  const getRefreshedPostflightEnvironmentVariables = React.useCallback(
    async (
      requestBody: OperationRequestBody,
      response: ResponseAndMetrics | CancelledResponse,
    ) => {
      if (response.type === 'cancelled') {
        return;
      }
      const environmentVariables = getEnvironmentVariables();
      let newEnvironmentVariables = environmentVariables;
      const runScriptOptions: Omit<
        Parameters<typeof runPreflightScript>[0],
        'script' | 'environmentVariables' | 'scriptType'
      > = {
        isTest: false,
        requestBody,
        responseBody: response.body,
        handlePrompt: (message, defaultValue) =>
          new Promise((resolve) => {
            setPromptRequest({
              message,
              defaultValue,
              onSubmit(result) {
                resolve(result);
                setPromptRequest(null);
              },
            });
          }),
        handleOAuth2Request: (authUrl, queryParams) =>
          new Promise((resolve) => {
            setOAuth2Request({
              authUrl,
              queryParams,
              onComplete(responseQueryParams) {
                resolve(responseQueryParams);
                setOAuth2Request(null);
              },
            });
          }),
      };
      try {
        newEnvironmentVariables = await runPreflightScript({
          script: postflightOperationScriptEditor.getValue(),
          environmentVariables: newEnvironmentVariables,
          scriptType: 'postflightOperation',
          ...runScriptOptions,
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }

      // if we should run operation script, run it and set result to newEnvVars
      if (!_.isEqual(environmentVariables, newEnvironmentVariables)) {
        setEnvironmentVariables(newEnvironmentVariables);
      }
      return newEnvironmentVariables;
    },
    [
      getEnvironmentVariables,
      runPreflightScript,
      setEnvironmentVariables,
      postflightOperationScriptEditor,
    ],
  );

  const getInterpolatedHeadersAndVariables = React.useCallback(
    async (requestBody: OperationRequestBody) => {
      const updatedEnvironmentVariables =
        await getRefreshEnvironmentVariables(requestBody);
      // update environmentVariables used in interpolation now because the state will not update until the next render from the localStorage update
      updateEnvironmentVariables(updatedEnvironmentVariables);
      return {
        headers: interpolateEnvVariablesIntoHeadersWithRef(checkedHeaders),
        variables: await traverseVariablesAndAttemptInterpolation(
          requestBody.variables ?? {},
          updatedEnvironmentVariables,
        ),
      };
    },
    [
      checkedHeaders,
      getRefreshEnvironmentVariables,
      interpolateEnvVariablesIntoHeadersWithRef,
      updateEnvironmentVariables,
    ],
  );

  const { hasPublicUrlBeenConfirmed, publicUrlConfirmationModalState } =
    useHasPublicUrlBeenConfirmed();

  const getUnprocessedVariables = React.useCallback(() => {
    let unprocessedVariables: JSONObject;
    try {
      unprocessedVariables = jsoncParser.parse(
        variablesEditor.getValue() || '{}',
      );
    } catch (e) {
      const error = e as Error;
      error.message = `Error parsing variables json: ${error.message}`;
      throw error;
    }
    if (
      Array.isArray(unprocessedVariables) ||
      typeof unprocessedVariables !== 'object'
    ) {
      throw new Error('Expected variables json to be an object');
    }
    return unprocessedVariables;
  }, [variablesEditor]);

  const performMockOrRealOperation = React.useCallback(
    async <TOnResponseUpdate extends OnResponseUpdate>({
      operationName,
      operationString,
      abortSignal,
      onResponseUpdate,
      isMultipartSubscription,
    }: {
      operationName: string | undefined;
      operationString: string;
      abortSignal: AbortSignal | undefined;
      onResponseUpdate: TOnResponseUpdate;
      isMultipartSubscription: boolean;
    }): Promise<PerformOperationReturn<TOnResponseUpdate>> => {
      if (isMockingResponses) {
        const mockResult = await performMockOperation({
          operationString,
          operationName,
          schema,
          editor,
        });
        if (onResponseUpdate) {
          onResponseUpdate(mockResult, true);
          return {
            type: 'response-updated-in-callback',
            status: mockResult.status,
          } as PerformOperationReturn<TOnResponseUpdate>;
        } else return mockResult as PerformOperationReturn<TOnResponseUpdate>;
      } else if (!endpointUrl) {
        throw MissingEndpointError;
      }

      const fileVariables = editorTabState.selectedTab
        ? editorTabState.tabbedFileVariableValues[editorTabState.selectedTab]
        : undefined;

      const unprocessedVariables = getUnprocessedVariables();

      const { headers: processedHeaders, variables: processedVariables } =
        await getInterpolatedHeadersAndVariables({
          query: operationString,
          variables: unprocessedVariables,
          operationName,
        });

      if (explorerBehavioralFlags.shouldPostMessageOperations) {
        return performPostMessageOperation({
          operationName,
          operationString,
          variables: processedVariables,
          processedHeaders,
          includeCookies,
          endpointUrl,
          onResponseUpdate,
          requestQueryPlan,
          showTraces,
          fileVariables,
          requestConnectorsDebugging,
        });
      }

      if (
        !(await hasPublicUrlBeenConfirmed({
          url: endpointUrl,
          preflightScript,
          shouldPrompt: true,
          canApproveSingleRun: true,
          isSubscription: false,
        }))
      ) {
        return {
          type: 'cancelled',
        } as PerformOperationReturn<TOnResponseUpdate>;
      }

      return performOperation({
        requestBody: {
          query: operationString,
          variables: processedVariables,
          operationName,
        },
        url: endpointUrl,
        processedHeaders,
        includeCookies,
        abortSignal,
        showTraces,
        requestQueryPlan,
        onResponseUpdate,
        isMultipartSubscription,
        fileVariables,
        requestConnectorsDebugging,
      });
    },
    [
      isMockingResponses,
      endpointUrl,
      editorTabState.selectedTab,
      editorTabState.tabbedFileVariableValues,
      getInterpolatedHeadersAndVariables,
      explorerBehavioralFlags.shouldPostMessageOperations,
      hasPublicUrlBeenConfirmed,
      preflightScript,
      includeCookies,
      showTraces,
      requestQueryPlan,
      schema,
      editor,
      getUnprocessedVariables,
      requestConnectorsDebugging,
    ],
  );

  const submitForResponseHints = React.useCallback(
    async (operation: string) => {
      if (
        !endpointUrl ||
        // ExplorerSettings should guard response hints from being on if the
        // url has not been confirmed. This check is here as a backup.
        !(await hasPublicUrlBeenConfirmed({
          url: endpointUrl,
          preflightScript,
          shouldPrompt: false,
          canApproveSingleRun: false,
          isSubscription: false,
        }))
      ) {
        return;
      }
      try {
        const hintsResponse = await performMockOrRealOperation({
          abortSignal: submitOperationAbortControllerRef.current?.signal,
          operationName: undefined,
          operationString: operation,
          isMultipartSubscription: false,
          onResponseUpdate: undefined,
        });
        if (
          hintsResponse.type === 'result' &&
          hintsResponse.body?.data &&
          typeof hintsResponse.body.data === 'object' &&
          !Array.isArray(hintsResponse.body.data)
        ) {
          return hintsResponse.body.data;
        }
      } catch (error) {
        // ignore error
      }
    },
    [
      endpointUrl,
      hasPublicUrlBeenConfirmed,
      preflightScript,
      performMockOrRealOperation,
    ],
  );

  const [_hasRunFirstQuery, setHasRunFirstQuery] =
    useLocalStorage('hasRunFirstQuery');
  const { dispatchResultAction: dispatchOperationResultAction } =
    editorTabState;
  const activeSubmissionIdRef = useRef<string>();
  const submitOperation = React.useCallback(
    async ({
      operationName,
      body,
    }: {
      operationName: string | undefined;
      body: string;
    }) => {
      dispatchOperationResultAction({ type: 'setLoading' });
      activeSubmissionIdRef.current = _.uniqueId();
      const currentSubmissionID = activeSubmissionIdRef.current;
      try {
        const operationString = editor.getValue();
        const variables = variablesEditor.getValue();
        const preflightOperationScript =
          preflightOperationScriptEditor.getValue();
        const postflightOperationScript =
          postflightOperationScriptEditor.getValue();

        submitOperationAbortControllerRef.current?.abort();
        submitOperationAbortControllerRef.current =
          new window.AbortController();
        const abortSignal = submitOperationAbortControllerRef.current.signal;

        const runId = _.uniqueId();
        // eslint-disable-next-line no-inner-declarations
        async function onResponseUpdate(
          response: ResponseAndMetrics | CancelledResponse,
        ) {
          setHasRunFirstQuery(true);
          if (response.type === 'cancelled') {
            submitOperationAbortControllerRef.current = undefined;
            return;
          }

          if (
            isOperationScriptEnabled &&
            shouldShowPostflightScripts &&
            !response.hasNext
          ) {
            await getRefreshedPostflightEnvironmentVariables(
              {
                query: body,
                variables: getUnprocessedVariables(),
                operationName,
              },
              response,
            );
          }

          if (currentSubmissionID === activeSubmissionIdRef.current) {
            setEndpointReachability('reachable');
            dispatchOperationResultAction({
              type: 'setResult',
              result: {
                type: 'response',
                data: response,
                operationName,
                operationString: body,
                runId,
              },
            });
            submitOperationAbortControllerRef.current = undefined;
          }
        }

        const { status } = await performMockOrRealOperation({
          abortSignal,
          operationName,
          operationString: body,
          onResponseUpdate,
          isMultipartSubscription: false,
        });
        if (currentSubmissionID === activeSubmissionIdRef.current) {
          pushToOperationHistory({
            operationName,
            operationString,
            variables,
            headers: tabbedHeaderDefinitions,
            script: preflightOperationScript,
            postflightOperationScript,
            timestamp: new Date().getTime(),
            responseStatus: status,
          });
        }
      } catch (e) {
        const error = e as Error;
        if (
          currentSubmissionID !== activeSubmissionIdRef.current ||
          // the request was presumably intentionally aborted by another operation
          // having been submitted before it came back.
          error.name === 'AbortError'
        ) {
          return;
        }
        if (error === MissingEndpointError) {
          openEditUrlModal('Run query');
        } else if (isPotentialCORSError(error)) {
          setEndpointReachability('possibleCorsError');
        } else {
          setEndpointReachability('reachable');
        }

        if (error instanceof TypeError && error.message === 'Failed to fetch') {
          dispatchOperationResultAction({
            type: 'setResult',
            result: {
              type: 'error',
              name: 'NetworkError',
              message: error.message,
            },
          });
          return;
        }

        dispatchOperationResultAction({
          type: 'setResult',
          result: {
            type: 'error',
            name: error.name,
            message: error.message,
            stack: error.stack,
          },
        });
      } finally {
        // aborted or stale responses should be ignored
        if (currentSubmissionID === activeSubmissionIdRef.current) {
          dispatchOperationResultAction({ type: 'resetToLastResult' });
        }
      }
    },
    [
      dispatchOperationResultAction,
      editor,
      variablesEditor,
      preflightOperationScriptEditor,
      postflightOperationScriptEditor,
      performMockOrRealOperation,
      setHasRunFirstQuery,
      setEndpointReachability,
      pushToOperationHistory,
      tabbedHeaderDefinitions,
      openEditUrlModal,
      getRefreshedPostflightEnvironmentVariables,
      isOperationScriptEnabled,
      shouldShowPostflightScripts,
      getUnprocessedVariables,
    ],
  );

  /**
   * Given an item in the operation history, make this the current operation
   */
  const loadOperation = React.useCallback(
    (historyItem: OperationHistoryItem) => {
      editor.setValue(historyItem.operationString);
      variablesEditor.setValue(historyItem.variables);
      setTabbedHeaderDefinitions(historyItem.headers);
      preflightOperationScriptEditor.setValue(historyItem.script);
      postflightOperationScriptEditor.setValue(
        historyItem.postflightOperationScript ?? '',
      );
      // Monaco doesn't support clearing selections, make a zero width selection
      // at the start.
      editor.setSelection(new monacoEditor.Range(1, 1, 1, 1));
      editor.setScrollTop(0);
    },
    [
      editor,
      variablesEditor,
      setTabbedHeaderDefinitions,
      preflightOperationScriptEditor,
      postflightOperationScriptEditor,
    ],
  );

  const localStorageGraphIdentifierForSubscriptionProtocol =
    useLocalStorageGraphIdentifier({
      shouldNamespaceSandboxByEndpoint: true,
      shouldOnlyStoreForRegisteredGraphs: false,
    });
  const [protocol] = usePerGraphIdentifierLocalStorage({
    key: 'graphqlSubscriptionProtocol',
    graphIdentifier: localStorageGraphIdentifierForSubscriptionProtocol,
    stableInitialValue: 'auto-detect',
  });

  const httpMultipartParams = useMemo(
    () => ({
      includeCookies,
      // TODO: If we want to support subscription query plan, traces, connectors debugging for
      // http multipart, we should pass through the user settings here
      requestQueryPlan: false,
      showTraces: false,
      requestConnectorsDebugging: false,
      abortSignalRef: submitOperationAbortControllerRef,
    }),
    [includeCookies, submitOperationAbortControllerRef],
  );
  const subscription = useSubscriptions({
    preflightScript,
    hasPublicUrlBeenConfirmed,
    getInterpolatedHeadersAndVariables,
    variablesEditor,
    setResultsTab,
    subscriptionUrl,
    shouldPostMessageOperations:
      explorerBehavioralFlags.shouldPostMessageOperations,
    protocol,
    httpMultipartParams,
  });

  return {
    oauth2Request,
    promptRequest,
    subscription,
    submitOperation,
    operationHistory,
    loadOperation,
    editor,
    variablesEditor,
    preflightOperationScriptEditor,
    postflightOperationScriptEditor,
    mockedResponseEditor,
    environmentVariablesEditor,
    editorTabState,
    resultsTab,
    setResultsTab,
    dispatchDocsNavAction,
    searchState,
    dispatchSearchAction,
    submitForResponseHints,
    hasPublicUrlBeenConfirmed,
    publicUrlConfirmationModalState,
  };
};
